/**
 * Measure tool state: the ruler, the guide lines, the display unit and the
 * toggles. The DOM lives in `measureOverlay.ts`; this file only holds
 * state, keeps it inside the viewport, and saves it to localStorage.
 *
 * All positions are CSS px of the lobby viewport. Guides, the ruler's
 * position and the settings survive a reload (`SavedState`); the overlay,
 * the ruler's visibility and the reference element always start fresh.
 */

import type { Box } from './elementBoxes';
import { clamp, isMeasureUnit, viewportSizeOn } from './units';
import type { MeasureAxis, MeasureReference, UnitSettings } from './units';

const STORAGE_KEY = 'qa-panel-measure-state';

export const MIN_RULER_LENGTH = 40;
const DEFAULT_RULER_LENGTH = 300;
/** Stops a bad stored value from creating a huge canvas. */
const MAX_RULER_LENGTH = 4000;
const MAX_GUIDES = 50;

export interface MeasureRuler {
    /** Position of the 0 tick, px from the viewport's left / top. */
    x: number;
    y: number;
    length: number;
    orientation: 'h' | 'v';
}

export interface MeasureGuide {
    id: string;
    /** `x` = vertical line at `pos` px from the left; `y` = horizontal line `pos` px from the top. */
    axis: MeasureAxis;
    pos: number;
    /** Placed by "wrap element"; the next wrap replaces these, manual guides stay. */
    wrap?: boolean;
}

/** `unit` and `reference` come from UnitSettings. */
export interface MeasureState extends UnitSettings {
    overlayOn: boolean;
    rulerVisible: boolean;
    ruler: MeasureRuler;
    guides: MeasureGuide[];
    snap: boolean;
    /** Outline elements whose edges sit on a guide (green) or just miss it (red). */
    highlightAligned: boolean;
}

/** The toggles and settings the panel changes through `set`. */
export type MeasureSettings = Partial<
    Pick<MeasureState, 'overlayOn' | 'rulerVisible' | 'unit' | 'snap' | 'highlightAligned'>
>;

/** What localStorage keeps. Everything else starts fresh each session. */
type SavedState = Pick<MeasureState, 'unit' | 'snap' | 'highlightAligned' | 'ruler' | 'guides'>;

const BOOLEAN_SETTINGS = ['overlayOn', 'rulerVisible', 'snap', 'highlightAligned'] as const;

const isNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

function defaultRuler(): MeasureRuler {
    const length = Math.min(DEFAULT_RULER_LENGTH, Math.max(MIN_RULER_LENGTH, window.innerWidth - 40));
    return {
        x: Math.round((window.innerWidth - length) / 2),
        y: Math.round(window.innerHeight / 3),
        length,
        orientation: 'h',
    };
}

/** Keeps at least a grabbable piece of the ruler on screen. */
function keepRulerOnScreen(ruler: MeasureRuler): MeasureRuler {
    const length = isNumber(ruler.length)
        ? clamp(Math.round(ruler.length), MIN_RULER_LENGTH, MAX_RULER_LENGTH)
        : DEFAULT_RULER_LENGTH;
    return {
        x: clamp(isNumber(ruler.x) ? ruler.x : 0, -ruler.length + MIN_RULER_LENGTH, window.innerWidth - MIN_RULER_LENGTH),
        y: clamp(isNumber(ruler.y) ? ruler.y : 0, 0, window.innerHeight - 8),
        length,
        orientation: ruler.orientation === 'v' ? 'v' : 'h',
    };
}

/** Keeps a guide inside the viewport so it can always be grabbed again. */
function keepGuideOnScreen(axis: MeasureAxis, pos: number): number {
    const viewportSize = viewportSizeOn(axis);
    return isNumber(pos) ? clamp(pos, 0, viewportSize) : Math.round(viewportSize / 2);
}

let guideCounter = 0;
function newGuideId(): string {
    guideCounter += 1;
    return `g${Date.now().toString(36)}${guideCounter}`;
}

function sameRuler(a: MeasureRuler, b: MeasureRuler): boolean {
    return a.x === b.x && a.y === b.y && a.length === b.length && a.orientation === b.orientation;
}

function defaultState(): MeasureState {
    return {
        overlayOn: false,
        unit: 'px',
        // Hidden until switched on with the panel's Ruler button.
        rulerVisible: false,
        ruler: defaultRuler(),
        guides: [],
        snap: true,
        highlightAligned: false,
        reference: null,
    };
}

// fallow-ignore-next-line unused-export
export class MeasureController {
    private state: MeasureState = defaultState();
    private onStateChange: (() => void) | null = null;

    init(onStateChange: () => void): void {
        this.onStateChange = onStateChange;
        this.state = { ...this.state, ...readSavedState() };
        onStateChange();
    }

    destroy(): void {
        this.onStateChange = null;
    }

    getState(): MeasureState {
        return {
            ...this.state,
            ruler: { ...this.state.ruler },
            guides: this.state.guides.map((guide) => ({ ...guide })),
            reference: this.state.reference ? { ...this.state.reference } : null,
        };
    }

    /** Toggles and settings from the panel. Invalid or unchanged values are ignored. */
    set(change: MeasureSettings): void {
        const next: MeasureSettings = {};
        for (const flag of BOOLEAN_SETTINGS) {
            const value = change[flag];
            if (typeof value === 'boolean' && value !== this.state[flag]) next[flag] = value;
        }
        if (isMeasureUnit(change.unit) && change.unit !== this.state.unit) next.unit = change.unit;
        // Showing the ruler implies the overlay — otherwise the click looks dead.
        if (next.rulerVisible) next.overlayOn = true;
        if (Object.keys(next).length > 0) this.save(next);
    }

    /** From the panel inputs and from the overlay once per drag / rotate / nudge. */
    setRuler(change: Partial<MeasureRuler>): void {
        const ruler = keepRulerOnScreen({ ...this.state.ruler, ...change });
        if (!sameRuler(ruler, this.state.ruler)) this.save({ ruler });
    }

    resetRuler(): void {
        this.save({ ruler: defaultRuler(), rulerVisible: true, overlayOn: true });
    }

    /** Adds a guide (at the viewport center by default) and turns the overlay on. */
    addGuide(axis: MeasureAxis, pos?: number): void {
        if (this.state.guides.length >= MAX_GUIDES) return;
        const center = Math.round(viewportSizeOn(axis) / 2);
        const guide = { id: newGuideId(), axis, pos: keepGuideOnScreen(axis, pos ?? center) };
        this.save({ guides: [...this.state.guides, guide], overlayOn: true });
    }

    setGuide(id: string, pos: number): void {
        const guide = this.state.guides.find((candidate) => candidate.id === id);
        if (!guide) return;
        const newPos = keepGuideOnScreen(guide.axis, pos);
        if (guide.pos === newPos) return;
        // A moved wrap guide becomes a manual one — QA placed it on purpose,
        // so the next wrap must not sweep it away.
        const moved = { id, axis: guide.axis, pos: newPos };
        this.save({ guides: this.state.guides.map((candidate) => (candidate.id === id ? moved : candidate)) });
    }

    removeGuide(id: string): void {
        const guides = this.state.guides.filter((guide) => guide.id !== id);
        if (guides.length !== this.state.guides.length) this.save({ guides });
    }

    /** Deletes the saved key and goes back to the defaults (overlay off). The key comes back on the next change. */
    clearSaved(): void {
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch {
            // localStorage may be unavailable in sandboxed contexts.
        }
        this.state = defaultState();
        this.onStateChange?.();
    }

    clearGuides(): void {
        if (this.state.guides.length > 0) this.save({ guides: [] });
    }

    /**
     * A guide on each edge of `box`, replacing the previous wrap's guides.
     * The wrapped element also becomes the reference for `em`.
     */
    wrapBox(box: Box, reference: MeasureReference): void {
        const manualGuides = this.state.guides.filter((guide) => !guide.wrap);
        const edges: Array<[MeasureAxis, number]> = [
            ['x', box.left],
            ['x', box.right],
            ['y', box.top],
            ['y', box.bottom],
        ];
        const wrapGuides = edges
            .slice(0, Math.max(0, MAX_GUIDES - manualGuides.length))
            .map(([axis, pos]) => ({ id: newGuideId(), axis, pos: keepGuideOnScreen(axis, pos), wrap: true }));
        this.save({ guides: [...manualGuides, ...wrapGuides], overlayOn: true, reference });
    }

    /** On window resize. Always broadcasts — % / vw / vh labels change even when nothing moved. */
    reclampForViewport(): void {
        this.save({
            ruler: keepRulerOnScreen(this.state.ruler),
            guides: this.state.guides.map((guide) => ({ ...guide, pos: keepGuideOnScreen(guide.axis, guide.pos) })),
        });
    }

    private save(change: Partial<MeasureState>): void {
        this.state = { ...this.state, ...change };
        const { unit, snap, highlightAligned, ruler, guides } = this.state;
        const saved: SavedState = { unit, snap, highlightAligned, ruler, guides };
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
        } catch {
            // localStorage may be unavailable in sandboxed contexts.
        }
        this.onStateChange?.();
    }
}

/** Whatever valid fields localStorage holds; anything missing or malformed is skipped. */
function readSavedState(): Partial<SavedState> {
    let saved: Record<string, unknown>;
    try {
        const parsed: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null');
        if (!parsed || typeof parsed !== 'object') return {};
        saved = parsed as Record<string, unknown>;
    } catch {
        return {};
    }

    const result: Partial<SavedState> = {};
    for (const flag of ['snap', 'highlightAligned'] as const) {
        const value = saved[flag];
        if (typeof value === 'boolean') result[flag] = value;
    }
    if (isMeasureUnit(saved['unit'])) result.unit = saved['unit'];

    const ruler = saved['ruler'] as Partial<MeasureRuler> | undefined;
    if (ruler && isNumber(ruler.x) && isNumber(ruler.y) && isNumber(ruler.length)) {
        result.ruler = keepRulerOnScreen({ x: ruler.x, y: ruler.y, length: ruler.length, orientation: ruler.orientation ?? 'h' });
    }

    if (Array.isArray(saved['guides'])) {
        result.guides = (saved['guides'] as Array<Partial<MeasureGuide> | null>)
            .filter((guide): guide is MeasureGuide =>
                !!guide && (guide.axis === 'x' || guide.axis === 'y') && isNumber(guide.pos),
            )
            .slice(0, MAX_GUIDES)
            .map((guide) => ({
                id: newGuideId(),
                axis: guide.axis,
                pos: keepGuideOnScreen(guide.axis, guide.pos),
                ...(guide.wrap === true ? { wrap: true } : null),
            }));
    }
    return result;
}
